import React, { ReactNode, useState, createContext } from 'react';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import { useIntl } from 'react-intl';
import UserBadge from '../../DashboardComponents/UserBadge';

interface OpenProfilesModalProps {
  profiles: string[];
  title: string;
}
interface ModalContextType {
  openProfilesModal: (props: OpenProfilesModalProps) => void;
}

export const ModalContext = createContext<ModalContextType>({
  openProfilesModal: () => {},
});

// This Modal Provider is required because we cannot mount a Modal inside an interactive element, and our cards are interactive elements.
// Rendering the modal directly in the cards component resulted in buggy behaviour.
// ref: https://meridian.a2z.com/components/modal/?platform=react-web&detailsTab=api#api-Modal
export const ModalProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const intl = useIntl();
  const [modalOpen, setModalOpen] = useState(false);
  const [profiles, setProfiles] = useState<string[]>([]);
  const [title, setTitle] = useState<string>('');

  const openProfilesModal = ({ profiles, title }: OpenProfilesModalProps) => {
    setModalOpen(true);
    setProfiles(profiles);
    setTitle(title);
  };

  return (
    <ModalContext.Provider value={{ openProfilesModal }}>
      {children}
      <Modal
        title={title}
        open={modalOpen}
        scrollContainer='viewport'
        onClose={() => setModalOpen(false)}
        closeLabel={intl.formatMessage({
          id: 'cs_perfportal_popover_close',
          defaultMessage: 'close',
        })}
        width={300}
      >
        {profiles.map(profile => {
          return (
            <Box key={profile}>
              <Row
                spacing='none'
                widths={['grid-2', 'grid-8', 'grid-2']}
                spacingInset='200 300 200 none'
              >
                <Box height='36px' width='36px'>
                  <UserBadge username={profile} givenName={profile} />
                </Box>
                <Box>
                  <Text>{profile}</Text>
                </Box>
              </Row>
            </Box>
          );
        })}
      </Modal>
    </ModalContext.Provider>
  );
};
